import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO, generateLocalBusinessSchema } from '../components/SEO';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { ServiceCard } from '../components/ServiceCard';
import { DifferentialCard } from '../components/DifferentialCard';
import { TestimonialCard } from '../components/TestimonialCard';
import { FAQSection } from '../components/FAQSection';
import { GoogleMapsSection } from '../components/GoogleMapsSection';
import { NeighborhoodList } from '../components/NeighborhoodList';
import { CTASection } from '../components/CTASection';
import { TrustSection } from '../components/TrustBuilding';
import { 
  siteConfig, 
  heroImages, 
  services, 
  differentials, 
  testimonials, 
  faqs,
  neighborhoods,
  beforeAfterGallery,
  galleryImages
} from '../data/mock';
import { ChevronRight, Star, Instagram } from 'lucide-react';

export const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemaData = generateLocalBusinessSchema({
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": testimonials.length,
      "bestRating": "5",
      "worstRating": "1"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Serviços de Estética Automotiva e Lava Jato",
      "itemListElement": services.slice(0, 6).map(service => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.name,
          "description": service.description
        }
      }))
    }
  });

  return (
    <>
      <SEO 
        title="Box7go Estética automotiva & Lava Jato Premium | Goiânia"
        description="Lavagem automotiva premium em Goiânia. Lavagem simples, completa e detalhada com acabamento impecável que dura até 30 dias."
        keywords="lava jato goiânia, lava jato perto de mim, estética automotiva goiânia, lavagem automotiva premium, lava jato jardim bela vista, lava jato aparecida de goiânia"
        canonicalUrl="https://box7go.com.br"
        ogImage="https://box7go.com.br/og-image.png"
        schemaData={schemaData}
      />

      {/* Hero Section - Premium & Conversion-Focused */}
      <section className="relative min-h-[700px] md:min-h-[800px] flex items-center bg-gradient-to-br from-black via-gray-900 to-[#00B4D8]/20">
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImages[0]})`,
            opacity: 0.3
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent z-0"></div>
        
        <div className="container mx-auto px-4 z-10 relative">
          <div className="max-w-4xl">
            {/* Badge/Tag */}
            <div className="inline-flex items-center gap-2 bg-[#00B4D8]/20 backdrop-blur-sm border border-[#00B4D8]/50 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#00B4D8] rounded-full animate-pulse"></span>
              <span className="text-white text-sm font-medium">Lava Jato e Estética Premium em Goiânia</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight">
              Seu carro <span className="text-[#00B4D8]">impecável</span> em tempo recorde
            </h1>
            
            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-white/90 mb-4 leading-relaxed font-light">
              Transformamos seu veículo com <strong className="text-white">produtos profissionais</strong>, <strong className="text-white">equipe especializada</strong> e acabamento impecável.
            </p>
            
            {/* Tagline */}
            <div className="flex items-center gap-3 mb-8">
              <div className="h-1 w-12 bg-[#00B4D8]"></div>
              <p className="text-2xl md:text-3xl font-bold text-[#00B4D8]">
                TEMPO DE PIT STOP. BRILHO DE PÓDIO.
              </p>
            </div>
            
            {/* Social Proof */}
            <div className="flex items-center gap-6 mb-8 text-white/80">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span className="font-semibold">5.0</span>
                <span className="text-sm">Avaliação</span>
              </div>
              <div className="h-4 w-px bg-white/30"></div>
              <div>
                <span className="font-semibold">1000+</span>
                <span className="text-sm ml-1">Clientes Satisfeitos</span>
              </div>
              <div className="h-4 w-px bg-white/30"></div>
              <div>
                <span className="font-semibold">18</span>
                <span className="text-sm ml-1">Bairros Atendidos</span>
              </div>
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <WhatsAppButton 
                text="Agendar pelo WhatsApp"
                className="text-lg px-10 py-5 shadow-2xl shadow-[#25D366]/50"
                messageType="booking"
              />
              <Link
                to="/lava-jato-perto-de-mim"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 hover:border-white text-white font-semibold px-10 py-5 rounded-lg transition-all duration-300"
              >
                Ver Todos os Serviços
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Building Section com Background */}
      <TrustSection />

      {/* Serviços - Foco em Lavagem */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nossos Serviços
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Do básico ao premium: escolha o cuidado ideal para seu veículo
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.filter(service => service.id !== 'estetica-automotiva').map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
          <div className="text-center mt-10">
            <WhatsAppButton 
              text="Falar com Especialista no WhatsApp"
              messageType="catalog"
              variant="large"
            />
          </div>
        </div>
      </section>

      {/* CTA 1 */}
      <CTASection 
        title="Pronto para deixar seu carro impecável?"
        subtitle="Solicite um orçamento personalizado pelo WhatsApp"
      />

      {/* Antes e Depois - Impactante */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#00B4D8]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00B4D8]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#00B4D8]/20 backdrop-blur-sm border border-[#00B4D8]/50 rounded-full px-4 py-2 mb-4">
              <span className="text-[#00B4D8] text-sm font-semibold">TRANSFORMAÇÃO REAL</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              Do Comum ao <span className="text-[#00B4D8]">Extraordinário</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Veja a diferença que faz trabalhar com profissionais de verdade
            </p>
          </div>
          
          {/* Before/After Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {beforeAfterGallery.map((item, index) => (
              <div key={index} className="group relative">
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-[#00B4D8]/50 transition-all duration-500 hover:scale-105">
                  {/* Images */}
                  <div className="relative h-80">
                    <div className="absolute inset-0 grid grid-cols-2 gap-1">
                      <div className="relative overflow-hidden">
                        <img 
                          src={item.before} 
                          alt={`${item.title} - Antes`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/20"></div>
                        <div className="absolute top-3 left-3 bg-red-500/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full">
                          ANTES
                        </div>
                      </div>
                      <div className="relative overflow-hidden">
                        <img 
                          src={item.after} 
                          alt={`${item.title} - Depois`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-3 right-3 bg-[#00B4D8]/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3 fill-current" />
                          DEPOIS
                        </div>
                      </div>
                    </div>
                    
                    {/* Divider Line */}
                    <div className="absolute inset-y-0 left-1/2 w-1 bg-white/30 transform -translate-x-1/2"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-xl">
                      <ChevronRight className="w-6 h-6 text-[#00B4D8]" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <div className="p-5 bg-gradient-to-b from-white/5 to-transparent">
                    <h3 className="font-bold text-white text-lg">{item.title}</h3>
                    <p className="text-gray-400 text-sm mt-1">Transformação completa</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* CTA */}
          <div className="text-center mt-12 space-y-4">
            <WhatsAppButton 
              text="Quero Este Resultado no Meu Carro"
              messageType="quote"
              variant="large"
              className="shadow-2xl shadow-[#25D366]/30"
            />
            <div>
              <Link
                to="/galeria"
                className="inline-flex items-center gap-2 text-[#00B4D8] hover:text-[#00B4D8]/80 font-semibold transition-colors"
              >
                Ver mais transformações na galeria
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Galeria de Fotos */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nosso Trabalho
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Qualidade e atenção aos detalhes em cada serviço
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div key={index} className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow aspect-square">
                <img 
                  src={image} 
                  alt={`Trabalho Box7go ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Avaliações */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              O que nossos clientes dizem
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Avaliações reais de quem confia no nosso trabalho
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* Bairros Atendidos */}
      <NeighborhoodList neighborhoods={neighborhoods} />

      {/* Instagram */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 p-1 rounded-2xl">
            <div className="bg-white rounded-xl p-8 md:p-12 text-center">
              <Instagram className="w-16 h-16 mx-auto mb-6 text-pink-500" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Siga-nos no Instagram
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Acompanhe nossos trabalhos, promoções e novidades diariamente
              </p>
              <a
                href={siteConfig.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
              >
                <Instagram className="w-6 h-6" />
                @box7.go
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mapa */}
      <GoogleMapsSection />

      {/* FAQ */}
      <FAQSection faqs={faqs} />

      {/* CTA Final */}
      <CTASection 
        title="Transforme seu veículo hoje mesmo"
        subtitle="Agende agora e experimente o melhor em estética automotiva"
        backgroundColor="bg-black"
      />
    </>
  );
};
